import React from 'react';
import { ArrowUpRight, ArrowDownRight, LucideIcon } from 'lucide-react';

export interface OverviewMetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: LucideIcon;
  trend?: {
    value: number;
    positive: boolean;
    label?: string;
  };
  gradient: string;
  format?: 'currency' | 'percentage' | 'count' | 'default';
  onClick?: () => void;
  loading?: boolean;
  className?: string;
}

export const OverviewMetricCard: React.FC<OverviewMetricCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  gradient,
  format = 'default',
  onClick,
  loading = false,
  className = '',
}) => {
  const formatValue = (val: string | number): string => {
    if (typeof val === 'string') return val;
    
    switch (format) {
      case 'currency':
        return `€${val.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      case 'percentage':
        return `${val.toFixed(1)}%`;
      case 'count':
        return val.toLocaleString('nl-NL');
      default:
        return val.toString();
    }
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 transition-all duration-200 ${
        onClick ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02]' : ''
      } ${loading ? 'opacity-60' : ''} ${className}`}
      onClick={onClick}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm z-10">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      )}
      
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1 truncate">
            {title}
          </p>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">
            {formatValue(value)}
          </p>
          {subtitle && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
              {subtitle}
            </p>
          )}
          {trend && (
            <div
              className={`flex items-center gap-1 mt-3 text-sm font-medium ${
                trend.positive
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`}
            >
              {trend.positive ? (
                <ArrowUpRight className="h-4 w-4 flex-shrink-0" />
              ) : (
                <ArrowDownRight className="h-4 w-4 flex-shrink-0" />
              )}
              <span>
                {Math.abs(trend.value).toFixed(1)}% {trend.label || 'vs last period'}
              </span>
            </div>
          )}
        </div>
        <div
          className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 ml-4`}
        >
          <Icon className="h-7 w-7 text-white" />
        </div>
      </div>
      <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient}`} />
    </div>
  );
};
